import { useState } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ProjectCard } from "./ProjectCard";
import { Project } from "@/types/project";
import { Profile } from "@/types/profile";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Sparkles, FolderPlus, Plus } from "lucide-react"; 
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNavigate } from "react-router-dom";

interface ProjectListProps {
  projects: Project[];
  profile: Profile | null;
  onNavigate: (id: string) => void;
  onShare: (project: Project) => void;
  onDuplicate: (project: Project) => void;
  onEdit: (project: Project) => void;
  onDelete: (id: string) => void;
  onArchive: (id: string) => void;
  onNewProject: () => void;
}

export function ProjectList({
  projects,
  profile,
  onNavigate,
  onShare,
  onDuplicate,
  onEdit,
  onDelete,
  onArchive,
  onNewProject,
}: ProjectListProps) {
  const [activeTab, setActiveTab] = useState<"active" | "archived">("active");
  const [archivingProject, setArchivingProject] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const filteredProjects = projects.filter(project => 
    activeTab === "active" ? !project.archived_at : !!project.archived_at
  );

  const isFreeUser = profile?.subscription_tier === 'free' || !profile?.subscription_tier;
  const activeProjectCount = projects.filter(p => !p.archived_at).length;
  const isAtProjectLimit = isFreeUser && activeProjectCount >= 1;
  const storageUsedPercent = profile ? (profile.storage_used / profile.storage_limit) * 100 : 0;
  const storageUsedGB = profile ? (profile.storage_used / 1073741824).toFixed(2) : '0';
  const storageLimitGB = profile ? (profile.storage_limit / 1073741824).toFixed(2) : '1';
  
  const handleArchive = async (id: string) => {
    try {
      setArchivingProject(id);
      await onArchive(id);
    } catch (error) {
      console.error("Error archiving project:", error);
      toast.error("Failed to archive project. Please try again.");
    } finally {
      setArchivingProject(null);
    }
  };

  const handleUpgradeClick = () => {
    navigate('/account');
  };

  return (
    <div className={`glass-card rounded-xl border border-white/5 shadow-lg backdrop-blur-md space-y-3 relative ${isMobile ? 'p-2 my-3' : 'p-5'}`}>
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as "active" | "archived")} className="w-full">
        <TabsList className="w-full max-w-md mx-auto grid grid-cols-2 glass-card mb-2">
          <TabsTrigger 
            value="active" 
            className="data-[state=active]:bg-primary/40 data-[state=active]:text-white data-[state=active]:shadow-md py-2 relative"
          >
            <span className="relative z-10">Active Projects</span>
            {activeTab === "active" && (
              <span className="absolute bottom-0 left-1/4 right-1/4 h-0.5 bg-white/80 rounded-full animate-fade-in" />
            )}
          </TabsTrigger>
          <TabsTrigger 
            value="archived" 
            className="data-[state=active]:bg-primary/40 data-[state=active]:text-white data-[state=active]:shadow-md py-2 relative"
          >
            <span className="relative z-10">Archived</span>
            {activeTab === "archived" && (
              <span className="absolute bottom-0 left-1/4 right-1/4 h-0.5 bg-white/80 rounded-full animate-fade-in" />
            )}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {isFreeUser && activeTab === "active" && (
        <Alert variant="default" className="bg-primary/10 border-primary/20 backdrop-blur-sm">
          <AlertCircle className="h-4 w-4 text-primary" />
          <AlertDescription className={`flex ${isMobile ? "flex-col space-y-2" : "items-center justify-between"}`}>
            <span>
              {isAtProjectLimit 
                ? "You've reached the project limit for free accounts."
                : "Free accounts are limited to 1 active project."}
            </span>
            <Button 
              variant="default" 
              size="sm"
              className={`bg-primary/90 hover:bg-primary shadow-[0_0_15px_rgba(139,92,246,0.3)] ${isMobile ? "w-full mt-1" : ""}`}
              onClick={handleUpgradeClick}
            >
              <Sparkles className="mr-2 h-4 w-4" />
              Upgrade Now
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {isFreeUser && storageUsedPercent > 75 && (
        <Alert variant="default" className="bg-primary/10 border-primary/20 backdrop-blur-sm">
          <AlertCircle className="h-4 w-4 text-primary" />
          <AlertDescription className={`flex ${isMobile ? "flex-col space-y-2" : "items-center justify-between"}`}>
            <span>
              Storage used: {storageUsedGB}GB of {storageLimitGB}GB ({storageUsedPercent.toFixed(1)}%)
            </span>
            <Button 
              variant="default" 
              size="sm"
              className={`bg-primary/90 hover:bg-primary shadow-[0_0_15px_rgba(139,92,246,0.3)] ${isMobile ? "w-full mt-1" : ""}`}
              onClick={handleUpgradeClick}
            >
              <Sparkles className="mr-2 h-4 w-4" />
              Get More Storage
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <ScrollArea className={`h-[calc(100vh-320px)] ${isMobile ? 'pr-2' : 'pr-4'}`}>
        <div className="grid gap-2 grid-cols-1">
          {filteredProjects.map((project, index) => (
            <div key={project.id} className="lazy-load-item" style={{ animationDelay: `${index * 50}ms` }}>
              <ProjectCard
                project={project}
                onNavigate={onNavigate}
                onShare={onShare}
                onDuplicate={onDuplicate}
                onEdit={onEdit}
                onDelete={onDelete}
                onArchive={handleArchive}
                isArchiving={archivingProject === project.id}
              />
            </div>
          ))}
          
          {filteredProjects.length === 0 && activeTab === "active" && (
            <div className={`text-center py-8 px-4 text-muted-foreground glass-card border border-white/5 flex flex-col items-center gap-${isMobile ? '4' : '4'}`}>
              <div className="p-4 bg-primary/10 rounded-full">
                <FolderPlus className="h-8 w-8 text-primary" />
              </div>
              <div className="text-center">
                <p className={`mb-${isMobile ? '3' : '3'}`}>Create your first project to get started!</p>
                <Button onClick={onNewProject} className="bg-primary hover:bg-primary/90 w-full sm:w-auto">
                  New Project
                </Button>
              </div>
            </div>
          )}
          
          {filteredProjects.length === 0 && activeTab === "archived" && (
            <div className="text-center p-8 text-muted-foreground glass-card border border-white/5">
              No archived projects
            </div>
          )}
          
          {isAtProjectLimit && activeTab === "active" && isFreeUser && (
            <div className="text-sm text-muted-foreground mt-2 p-4 glass-card border border-white/5 rounded-lg">
              During our beta period, free users are limited to one active project at a time. 
              Upgrade to {profile?.subscription_tier === 'free' ? 'Pro' : 'Premium'} for unlimited projects, 
              or archive your current project before creating a new one.
            </div>
          )}
        </div>
        
        <div className={`${isMobile ? 'h-24' : 'h-6'}`}></div>
      </ScrollArea>
    </div>
  );
}
